/* eslint-disable max-lines-per-function, max-lines */
import { EventBus, Events, store } from "jiffy-store";
import { track } from "jiffy-analytics";
import collectFormItemsQty from "common/utils/form/collect-form-items-qty";
import collectFormDtfSizes from "common/utils/form/collect-form-dtf-sizes";
import mobileApp from "common/utils/mobile/app";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import { observeElementVisibility, isScrollOnTop } from "common/utils/ui/visibility";
import { getQueryParams, requestLocalStorage, redirectTo } from "common/utils/browser";
import madeStore from "components/jiffy/vue-product-details-print/store/store";
import { MODES, NECK_LINE_MODES } from "components/jiffy/vue-product-details-print/misc/constants";
import { incrementHeaderCounter } from "components/jiffy/masthead-cart/masthead-cart";
import { toQueryString, makeFullPath } from "jiffy-api";
import idleCallback from "common/utils/ui/idle_callback";
import { refreshCsrfToken } from "common/utils/csrf-token-manager";
import { isMadePdpOrMadeMode } from "components/jiffy/vue-product-details-print/utils/made-utils";
import { addToCartAnalytics, triggerPdpVisitEvent, trackSizeQuantityMissed } from "./analytics";
import { getPriority } from "./extract-priority";
import productPage from "./product-page";
import updateSellingPrices from "./update-variant-selling-price";
import updateRestockText from "./update-restock-text";
import createPayload from "./show-product-modules/payload";
import validationErrors from "./show-product-modules/validation-errors";
import updateFlashStock from "./update-flash-stock";

const setSubmitButtonLoading = isLoading => {
  const button = document.querySelector(".js-add-to-cart");
  if (!button) return;

  if (isLoading) {
    button.classList.add("product-details-variants-form__submit--loading");
  } else {
    button.classList.remove("product-details-variants-form__submit--loading");
  }
};

const parsePayloadString = payloadStr => {
  // Just convert URL params to a flat object for logging
  // From: "variants[4448][quantity]=1&product_id=1319"
  // To: { "variants[4448][quantity]": "1", "product_id": "1319" }
  const params = new URLSearchParams(payloadStr);
  const result = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

const tokenRefreshHandler = async (context, dataToSubmit, error) => {
  const source = "add_to_cart";
  const success = await refreshCsrfToken(source);
  if (success) {
    setSubmitButtonLoading(true);
    // Keep the original ajax call since it's part of a larger jQuery ajax setup
    $.ajax(context);
  } else {
    track("add_to_cart_error", {
      error: "Token refresh failed",
      code: error.status,
      error_message: error.responseJSON?.error_message || error?.responseText,
      tokenRetries: context.tokenRetryCount,
      networkRetries: context.tryCount,
      payload: parsePayloadString(dataToSubmit)
    });
    redirectTo("/login");
  }
};

const showProduct = {
  setActiveColorName(value) {
    $("#active-color-name").val(value);
    this.productSelectedColor.setActiveColor(value);
  },
  getActiveColorName() {
    return $("#active-color-name").val();
  },
  productInputGrid: {
    onSubmit($form, { dtfParams, madeParams }) {
      if (!madeParams.isEdit) {
        madeStore.dispatch("resetState");
      }

      mobileApp.mq.publish("feedback", { type: "vibration" });

      const isMadeEditing = Boolean(madeParams.isMade && madeParams.isEdit);
      const { path: formPath, method } = (() => {
        if (!madeParams.isMade) return { path: "/api/cart/add", method: "POST" };
        if (madeParams.isEdit) {
          const { asgid } = getQueryParams();
          return { path: `/api/pdp/${asgid}/made_update`, method: "PUT" };
        }
        return { path: "/api/cart/made_add", method: "POST" };
      })();
      const cartAddURL = makeFullPath(formPath);

      const eventId = document.querySelector(productPage.selectors.formEventId).value;
      const dataToSubmit = createPayload($form, {
        dtfParams,
        madeParams: {
          ...madeParams,
          madePayload: madeParams.madePayload?.filter(p => !p.isEnhancement)
        }
      });
      const isSendingFormData = dataToSubmit instanceof FormData;

      setSubmitButtonLoading(true);

      $.ajax({
        type: method,
        url: cartAddURL,
        data: dataToSubmit,
        processData: isSendingFormData ? false : undefined,
        contentType: isSendingFormData ? false : undefined,
        tryCount: 0,
        retryLimit: 3,
        tokenRetryCount: 0,
        tokenRetryLimit: 3,
        retryDelay: 1_000, // 1 second
        beforeSend(xhr) {
          productPage.variantDomStructure.ready = false;
          return xhr.setRequestHeader("X-CSRF-Token", $('meta[name="csrf-token"]').attr("content"));
        },
        error(error, textStatus, errorThrown) {
          if (
            (error.status === 0 && error.readyState === 0 && textStatus !== "abort") ||
            error.status === 502 ||
            error.status === 504
          ) {
            this.tryCount += 1;
            if (this.tryCount <= this.retryLimit) {
              // Retry after some time with exponential back-off
              const delay = this.retryDelay * 2 ** (this.tryCount - 1);
              setTimeout(() => {
                $.ajax(this);
              }, delay);
              return;
            }
          }

          let errorResponse;

          if (error.responseText && error.responseText.trim() !== "") {
            errorResponse = JSON.parse(error.responseText);
          } else {
            errorResponse = {
              error: "Empty response",
              error_message: "Processing Problem. Please refresh the page."
            };
          }

          // Handle token/session expiration
          if (
            error.status === 422 &&
            (errorResponse?.error === "Session expired" ||
              errorResponse?.error === "Empty response")
          ) {
            this.tokenRetryCount += 1;
            if (this.tokenRetryCount <= this.tokenRetryLimit) {
              tokenRefreshHandler(this, dataToSubmit, error);
              return;
            }
          }

          setSubmitButtonLoading(false);

          // If we've exhausted all retries or have a different error
          $("#continue-shopping-modal").modal("hide");
          $("#adding-failure").modal("show");
          $("#adding-failure").find("#adding-failure-error").html(errorResponse?.error_message);

          const payloadData = parsePayloadString(dataToSubmit);
          const errorDetails = Object.fromEntries(
            Object.getOwnPropertyNames(error || {}).map(key => [key, error[key]])
          );

          // Simple diagnostics to determine if it's a network issue
          const diagnostics = {
            onlineStatus: navigator.onLine,
            userAgent: navigator.userAgent,
            connectionType: navigator.connection?.effectiveType,
            isWebView: /WebView|wv/.test(navigator.userAgent),
            likelyCause: navigator.onLine ? "blocking_or_other" : "network_offline"
          };

          window.Honeybadger?.notify(errorResponse?.error, {
            context: {
              tags: "add_to_cart,frontend"
            },
            params: {
              error_message: errorResponse?.error_message,
              status_code: error.status,
              payload: payloadData,
              tokenRetries: this.tokenRetryCount,
              networkRetries: this.tryCount,
              errorDetails
            }
          });

          track("add_to_cart_error", {
            error: error.responseText,
            code: error.status,
            payload: payloadData,
            tokenRetries: this.tokenRetryCount,
            networkRetries: this.tryCount,
            errorDetails,
            diagnostics,
            textStatus,
            errorThrown
          });
        },
        success(data) {
          const amount = Object.values(dtfParams.formItemsQty).reduce((a, b) => a + b) || 0;
          incrementHeaderCounter(amount);
          $form.reset();
          productPage.resetEventId();
          productPage.setLineItems(data.line_items);
          productPage.variantDomStructure.ready = true;

          EventBus.publish(Events.ITEMS_ADDED_TO_CART, data);

          const originalCartTotal = parseFloat(data.cart_total);
          store.pdp.change({ originalCartTotal });

          productPage.variantPricesDataLoadPromise().then(() => {
            updateSellingPrices(
              getPriority(originalCartTotal),
              document.getElementById("product-details-quantity-form"),
              { formSubmit: true }
            );
          });

          store.cart.change({ items: data.line_items });

          if (isMadeEditing) {
            redirectTo("/cart");
          } else {
            setSubmitButtonLoading(false);
          }
        }
      });

      EventBus.publish(Events.MOBILE_GRID_SUBMIT, { form: $form, madeParams });
      addToCartAnalytics(
        {
          itemsQty: dtfParams.formItemsQty,
          dtfSizes: dtfParams.formDtfSizes,
          eventId
        },
        isMadePdpOrMadeMode()
      );
    },
    publishInputUpdate(input) {
      const { variantId } = input.dataset;
      const form = input.closest("form");
      const urlParams = new URLSearchParams(window.location.search);
      const colorName = urlParams.get("ac");

      let totalValue = 0;
      const variantsQuantity = {};
      Array.from(form.querySelectorAll(".js-quantity-form-input")).forEach(elem => {
        const inputValue = parseInt(elem.value, 10);
        const parsedValue = !Number.isNaN(inputValue) ? inputValue : 0;
        totalValue += parsedValue;

        variantsQuantity[elem.dataset.variantId] = parsedValue;
      });

      EventBus.publish(
        Events.PRODUCT_INPUT_CHANGED,
        form,
        totalValue,
        colorName,
        variantId,
        variantsQuantity
      );
    },
    changeEventProductQuantityInputs() {
      const $inputsWrapper = document.getElementById("js-product-mobile-grid");
      if (!$inputsWrapper || $inputsWrapper.dataset.listeners) return;

      $inputsWrapper.addEventListener("change", event => {
        const input = event.target;
        this.publishInputUpdate(input);
      });

      $inputsWrapper.addEventListener("input", event => {
        const input = event.target;
        input.classList.toggle("has-value", input.value !== "");
        input.setAttribute("data-value", input.value);
      });

      $inputsWrapper.dataset.listeners = true;
    },
    init() {
      const validation = validationErrors();
      const formSelector = ".js-product-details-quantity-form";
      const $forms = document.querySelectorAll(formSelector);

      if (isScrollOnTop()) {
        observeElementVisibility(
          formSelector,
          (entries, scrolled) => {
            track("viewed_on_pdp", { target: "qty_grid", scrolled });
          },
          { mobileOnly: true, once: true, observerOptions: { threshold: 0.2 } }
        );
      }

      $forms.forEach($form => {
        $form?.addEventListener("submit", event => {
          event.preventDefault();
          const madeParams = {
            isMade: madeStore?.getters?.["app/mode"] === MODES.MADE,
            isEdit: Boolean(madeStore?.getters?.config?.is_edit),
            madePayload: madeStore?.getters?.["checkout/checkoutPayload"],
            frontInput: $form.elements["field-artwork-upload-front"],
            backInput: $form.elements["field-artwork-upload-back"],
            sample: $form.elements.sample
          };

          const formItemsQty = collectFormItemsQty($($form));
          const formDtfSizes = collectFormDtfSizes($($form));
          const formDtfImage = $form.querySelector(".js-dz-thumbnail")?.src;

          idleCallback(() => {
            EventBus.publish(Events.ADD_TO_CART, {
              items: formItemsQty,
              eventAction: "Quick"
            });
          });

          const hasMadeErrors =
            madeParams.isMade && jQuery.isEmptyObject(madeParams.madePayload) && !madeParams.isEdit;
          const hasFormErrors = jQuery.isEmptyObject(formItemsQty);
          const hasDimensionsErrors =
            madeParams.isMade && madeStore?.getters?.["checkout/hasInvalidDimensions"];

          const artworkWithMissingExpertEditAddonNote = (() => {
            if (!madeParams.isMade) return false;

            if (!window.jiffy_made_config?.paid_expert_edit_addon_enabled) return false;

            const frontArtwork = madeStore?.getters?.["artwork_front/artworkCamelCase"];
            const backArtwork = madeStore?.getters?.["artwork_back/artworkCamelCase"];

            const frontError =
              frontArtwork?.enhancementTier === "full_enhancements" &&
              frontArtwork?.hasExpertEditAddon &&
              !frontArtwork?.enhancementNote?.trim();

            const backError =
              backArtwork?.enhancementTier === "full_enhancements" &&
              backArtwork?.hasExpertEditAddon &&
              !backArtwork?.enhancementNote?.trim();

            if (frontError) return frontArtwork;
            if (backError) return backArtwork;
            return null;
          })();

          if (hasDimensionsErrors) {
            track("jiffy_made_incorrect_dimensions", madeParams?.madePayload);
          }

          if (hasFormErrors) {
            trackSizeQuantityMissed();
          }

          if (hasFormErrors || hasMadeErrors || hasDimensionsErrors) {
            validation.show({ hasMadeErrors, hasFormErrors, hasDimensionsErrors });
          } else if (artworkWithMissingExpertEditAddonNote) {
            document
              .querySelector(
                `.js-print-perfect-redraw-notes-addon[data-id="${artworkWithMissingExpertEditAddonNote.id}"]`
              )
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          } else {
            setSubmitButtonLoading(true);
            const dtfParams = { formItemsQty, formDtfSizes, formDtfImage };
            idleCallback(() => {
              this.onSubmit($form, { dtfParams, madeParams });
            }, 0); // zero is intentional (trying to move to a separate task and then wait for the next idle)
          }

          if (madeParams.isMade) {
            const payload = {};
            payload.placement_location =
              (!madeParams.frontInput && !madeParams.backInput && "both") ||
              (!madeParams.frontInput && "front") ||
              "back";
            const expertEditAddonEnabledOnSides = {
              front: false,
              back: false
            };

            if (document.querySelector(".js-ab-made-monetization-enabled")) {
              if (!madeParams.frontInput) {
                payload.enhancement_tier_front =
                  madeStore.getters["artwork_front/artworkCamelCase"].enhancementTier;
                expertEditAddonEnabledOnSides.front =
                  madeStore.getters["artwork_front/artworkCamelCase"].hasExpertEditAddon;
              }
              if (!madeParams.backInput) {
                payload.enhancement_tier_back =
                  madeStore.getters["artwork_back/artworkCamelCase"].enhancementTier;
                expertEditAddonEnabledOnSides.back =
                  madeStore.getters["artwork_back/artworkCamelCase"].hasExpertEditAddon;
              }

              payload.expert_edit_addon_enabled_on_sides = expertEditAddonEnabledOnSides;
            }

            if (madeParams.isEdit) {
              track("made_pdp_save_changes_clicked", payload);
            } else {
              track("made_pdp_add_to_cart_clicked", payload);
            }
          }
        });
        productPage.variantDomStructure.domLoaded = true;
        productPage.cacheVariantDom();
        this.changeEventProductQuantityInputs();
        EventBus.publish(Events.ORDER_TIER_CHANGED);
      });
    }
  },
  productSelectedColor: {
    setActiveColor(activeColorName, pdpMadeMode = false) {
      const $placeholders = document.querySelectorAll(".js-form-loading-placeholder");
      const jiffyPrint = pdpMadeMode || "";
      const $activePlaceholder = document.querySelector(
        `.js-form-loading-placeholder[data-color-name="${activeColorName}"][data-jiffy-print="${jiffyPrint}"]`
      );

      if ($activePlaceholder) {
        $placeholders.forEach($placeholder => $placeholder.classList.add("hidden"));
        $activePlaceholder.classList.remove("hidden");
        updateFlashStock();
      }
    }
  },
  productVariantColorModal: {
    selectColor(colorData, previousColorGroup) {
      const {
        color: selectedColor,
        colorGroup: newColorGroup,
        showEstimates,
        compositionId,
        softness = "{}"
      } = colorData;

      showProduct.setActiveColorName(selectedColor);

      updateRestockText(selectedColor);

      if (requestLocalStorage()?.getItem("PDPcolorPickerClicked")) {
        requestLocalStorage()?.setItem("compositionReferrer", "PDPColorPicker");
      } else {
        requestLocalStorage()?.setItem("compositionReferrer", "PDPColorModal");
      }

      requestLocalStorage()?.removeItem("PDPcolorPickerClicked");

      const variantId = triggerPdpVisitEvent(selectedColor);

      if (variantId) {
        this.setCanonicalURL(selectedColor);
      }

      madeStore.dispatch("app/setActiveComposition", { compositionId, color: selectedColor });

      idleCallback(() => {
        let parsedSoftness;
        try {
          parsedSoftness = JSON.parse(softness);
        } catch {
          parsedSoftness = {};
        }

        EventBus.publish(Events.PRODUCT_COLOR_SELECTED, this, {
          color: selectedColor,
          softness: parsedSoftness,
          showEstimates,
          newColorGroup,
          previousColorGroup,
          compositionId
        });
      });
      this.updateDocumentTitle(selectedColor);
    },

    initColorSelectorHandler() {
      const $colorSelectorToggle = $(".js-color-selector");
      const $modal = $("#products-color-popup");
      const $variantContent = $("#color-variant-selector-content");

      $colorSelectorToggle.on("click", e => {
        e.preventDefault();
        EventBus.publish(Events.PRODUCT_STATIC_COLOR_SELECTED);
      });

      $variantContent.on("click", "a.js-color-variant-selector", event => {
        if (event.currentTarget.pathname !== window.location.pathname) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget;

        const previousColorGroup = $variantContent
          .find("a.js-color-variant-selector.product-color-picker__item--active")
          .data("colorGroup");
        $variantContent
          .find("a.js-color-variant-selector")
          .removeClass("product-color-picker__item--active");

        showProduct.productVariantColorModal.selectColor(target.dataset || {}, previousColorGroup);
        $(target).addClass("product-color-picker__item--active");

        $modal.modal("hide");
        window.history.replaceState(null, null, `${target.pathname}${target.search}`);
      });
    },

    subscribeToColorChangeFromCarousel() {
      EventBus.on(Events.PRODUCT_COLOR_SELECTED_FROM_SLIDER, (evt, { color }) => {
        // Next event loop
        setTimeout(() => {
          const $variantContent = $("#color-variant-selector-content");
          $variantContent
            .find("a.js-color-variant-selector")
            .removeClass("product-color-picker__item--active");
          $variantContent
            .find(`[data-color="${color}"]`)
            .addClass("product-color-picker__item--active");
        });
      });
    },

    subscribeOnPageMode() {
      const handleModeChange = mode => {
        const $madeWrapper = document.querySelector(".js-product-details-made-wrapper");
        $madeWrapper?.classList.toggle("empty", mode !== MODES.MADE);
        showProduct.setActiveColorName(showProduct.getActiveColorName());
      };
      madeStore.watch(state => state.app.mode, handleModeChange, { immediate: true });
    },

    subscribeOnMadeMode() {
      const handleMadeMode = () => {
        showProduct.setActiveColorName(showProduct.getActiveColorName());
      };
      madeStore.watch(state => state.app.mode, handleMadeMode, { immediate: true });
    },

    subscribeOnUploadingArtwork() {
      const isEdit = Boolean(window.jiffy_made_config?.is_edit);
      const addToCartText = isEdit ? "Save Changes" : "Add to Cart";

      if (document.querySelector(".js-ab-made-monetization-enabled")) {
        // Subscribe for mutations for ArtworksState
        madeStore.subscribe(() => {
          const { getters } = madeStore;

          const isMade = getters["app/mode"] === MODES.MADE;
          const isSupports = getters["app/supportsMade"];
          const frntArtCmlCase = getters["artwork_front/artworkCamelCase"];
          const backArtCmlCase = getters["artwork_back/artworkCamelCase"];
          const frontEnhanced =
            getters["artwork_front/artwork"]?.enhancement_tier === "print_original" ||
            getters["artwork_front/enhanced"];

          const backEnhanced =
            getters["artwork_back/artwork"]?.enhancement_tier === "print_original" ||
            getters["artwork_back/enhanced"];
          // determine the loading state
          const frontLoading =
            getters["artwork_front/uploadArtwork"] ||
            (!frntArtCmlCase?.isSimulated && (!frontEnhanced || !getters["artwork_front/isValid"]));
          const backLoading =
            getters["artwork_back/uploadArtwork"] ||
            (!backArtCmlCase?.isSimulated && (!backEnhanced || !getters["artwork_back/isValid"]));

          const isLoading = isMade && (frontLoading || backLoading || !isSupports);

          const isEmptyFrntComment =
            frntArtCmlCase?.enhancementTier === "custom" && !frntArtCmlCase?.enhancementNote;
          const isEmptyBackComment =
            backArtCmlCase?.enhancementTier === "custom" && !backArtCmlCase?.enhancementNote;
          const isCommentEmpty = isMade && (isEmptyFrntComment || isEmptyBackComment);

          const $submitButton = document.querySelector(".js-add-to-cart");
          const $submitButtonText = document.querySelector(".js-add-to-cart-submit-text");

          if (!$submitButton) return;
          if (isLoading) {
            $submitButton.setAttribute("disabled", "disabled");
            $submitButtonText.textContent = "Preparing image…";
          } else if (isCommentEmpty) {
            $submitButton.setAttribute("disabled", "disabled");
            $submitButtonText.textContent = addToCartText;
          } else {
            $submitButton.removeAttribute("disabled");
            $submitButtonText.textContent = addToCartText;
          }
        });
        return;
      }

      let changedToMade = false;
      madeStore.watch(
        state => [
          state.app.mode,
          state.artwork_front.uploadArtwork,
          state.artwork_back.uploadArtwork
        ],
        ([newMode, frontUploaded, backUploaded], [oldMode]) => {
          if (newMode === MODES.MADE && oldMode === MODES.BLANKS) {
            changedToMade = true;
          }

          if (changedToMade && frontUploaded) {
            ScrollToElement.scrollIntoViewBySelector(".js-pdp-experience-switcher");
            changedToMade = false;
          }

          if (changedToMade && backUploaded) {
            ScrollToElement.scrollIntoViewBySelector(".js-pdp-back-artwork-placement");
            changedToMade = false;
          }
        }
      );
    },

    subscribeOnUpdatingQuantityGrid() {
      EventBus.on(Events.PRODUCT_INPUT_CHANGED, ({ target: form }) => {
        const sizes = Array.from(form.getElementsByClassName("js-quantity-form-input")).reduce(
          (acc, input) => {
            const quantity = input.value;
            const { size } = input.dataset;

            if (quantity && acc.indexOf(size) === -1) {
              acc.push(size);
            }

            return acc;
          },
          []
        );
        madeStore.commit("app/setFilledSizes", sizes);
        if (sizes.length === 0) {
          madeStore.commit("surface_front/setNeckLineMode", NECK_LINE_MODES.DEFAULT);
          madeStore.commit("surface_back/setNeckLineMode", NECK_LINE_MODES.DEFAULT);
        }
      });
    },

    updateDocumentTitle(color) {
      const colors = Array.from(document.querySelectorAll(".js-color-variant-selector")).map(
        item => item.dataset.color
      );

      if (colors.length) {
        const regExp = new RegExp(`(${colors.join("|")})`, "g");
        document.title = document.title.replace(regExp, color);
      }
    },
    setCanonicalURL(selectedColor) {
      const canonical = document.querySelector("link[rel='canonical']");

      if (canonical) {
        const queryString = toQueryString({ ac: selectedColor });
        const canonicalURL = window.location.href.replace(/\?.*/, `?${queryString}`);
        canonical.setAttribute("href", canonicalURL);
      }
    },
    init() {
      this.initColorSelectorHandler();
      this.subscribeToColorChangeFromCarousel();
      this.subscribeOnPageMode();
      this.subscribeOnMadeMode();
      this.subscribeOnUploadingArtwork();
      this.subscribeOnUpdatingQuantityGrid();
    }
  },
  init() {
    if (!this.getActiveColorName()) {
      return;
    }
    this.productInputGrid.init();
    this.productVariantColorModal.init();
    window.localStorage.removeItem("compositionReferrer");
  },
  changeEventProductQuantityInputs() {
    this.productInputGrid.changeEventProductQuantityInputs();
  }
};

export default showProduct;
